import { AppNotificationDto } from '@act/shared/data-transfer-objects';
import {
  AppNotificationDeletedEventPayload,
  AppNotificationEventTypes,
  AppNotificationUpsertedEventPayload,
} from '@act/shared/socket-events';
import {
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { LoadingStatus } from 'redux/types/sharedTypes';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { appNotificationRequests } from '../requests/appNotification.requests';
import { useAppSelector } from '../storeHooks';
import {
  AppNotificationGroupPageState,
  AppNotificationState,
} from '../types/appNotificationTypes';
import { createApiThunk } from '../utils/apiThunk';
import { verifyUser } from './actingUserSlice.slice';
import type { WebsocketEventActions } from '../types/websocketTypes';
import { resetRedux } from '../actions/sharedActions';
import { selectAppNotificationListFromAdapter } from './appNotificationSelectors.util';

// THUNKS/ACTIONS ==============================================================
export const getAppNotificationGroupTypes = createApiThunk(
  appNotificationRequests.getAppNotificationGroupTypes
);
export const getAppNotificationsForGroup = createApiThunk(
  appNotificationRequests.getAppNotificationsForGroup
);
export const loadMoreAppNotificationsForGroup = createApiThunk(
  appNotificationRequests.loadMoreAppNotificationsForGroup
);
export const markAppNotificationsAsSeen = createApiThunk(
  appNotificationRequests.markAppNotificationsAsSeen
);
export const deleteAppNotifications = createApiThunk(
  appNotificationRequests.deleteAppNotifications
);

// INITIAL STATE ===============================================================
const appNotificationsEntityAdapter = createEntityAdapter<AppNotificationDto>();
const initialState: AppNotificationState = {
  appNotifications: appNotificationsEntityAdapter.getInitialState(),
  appNotificationGroups: null,
  groupStates: {},
};

// SLICE =======================================================================
export const appNotificationSlice = createSlice({
  name: 'appNotifications',
  initialState,
  reducers: {
    deleteAppNotifications: (
      state: AppNotificationState,
      action: PayloadAction<AppNotificationDeletedEventPayload>
    ) => {
      action.payload.appNotificationIds.forEach(id => {
        const n = state.appNotifications.entities[id];
        if (
          n?.notificationGroupType &&
          state.groupStates[n.notificationGroupType]
        ) {
          state.groupStates[n.notificationGroupType].totalCount = Math.max(
            0,
            state.groupStates[n.notificationGroupType].totalCount - 1
          );
        }
      });
      appNotificationsEntityAdapter.removeMany(
        state.appNotifications,
        action.payload.appNotificationIds
      );
    },
    upsertAppNotifications: (
      state: AppNotificationState,
      action: PayloadAction<AppNotificationUpsertedEventPayload>
    ) => {
      action.payload.appNotificationView.notifications.forEach(n => {
        const isNew = !state.appNotifications.ids.includes(n.id);
        const key = n.notificationGroupType;
        if (isNew && key) {
          if (state.groupStates[key]) {
            state.groupStates[key].totalCount += 1;
          } else {
            // Notification arrived before this group's initial fetch completed;
            // seed a stub so the badge count stays accurate.
            state.groupStates[key] = {
              status: 'initial',
              loadMoreStatus: 'initial',
              hasMore: false,
              fetchedCount: 0,
              totalCount: 1,
            };
          }
        }
      });
      appNotificationsEntityAdapter.upsertMany(
        state.appNotifications,
        action.payload.appNotificationView.notifications
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getAppNotificationGroupTypes.fulfilled, (state, action) => {
        state.appNotificationGroups = action.payload.notificationGroups;
      })
      .addCase(getAppNotificationsForGroup.pending, (state, action) => {
        const key = action.meta.arg.params?.groupKey;
        if (!key) return;
        const existing = state.groupStates[key];
        if (existing) {
          // Preserve totalCount (and any realtime stub) so the badge does not
          // flash to 0 while a refetch is in flight.
          existing.status = 'loading';
          existing.loadMoreStatus = 'initial';
        } else {
          state.groupStates[key] = {
            status: 'loading',
            loadMoreStatus: 'initial',
            hasMore: false,
            fetchedCount: 0,
            totalCount: 0,
          };
        }
      })
      .addCase(getAppNotificationsForGroup.fulfilled, (state, action) => {
        const key = action.meta.arg.params?.groupKey;
        if (!key) return;
        // Merge the page; do not wipe the group. Websocket upserts that arrived
        // while this request was in flight must stay in the store.
        appNotificationsEntityAdapter.upsertMany(
          state.appNotifications,
          action.payload.notifications
        );
        state.groupStates[key] = {
          status: 'success',
          loadMoreStatus: 'initial',
          hasMore: action.payload.hasMore,
          fetchedCount: action.payload.notifications.length,
          totalCount: action.payload.totalCount,
        };
      })
      .addCase(getAppNotificationsForGroup.rejected, (state, action) => {
        const key = action.meta.arg.params?.groupKey;
        if (key && state.groupStates[key]) {
          state.groupStates[key].status = 'failed';
        }
      })
      .addCase(loadMoreAppNotificationsForGroup.pending, (state, action) => {
        const key = action.meta.arg.params?.groupKey;
        if (key && state.groupStates[key]) {
          state.groupStates[key].loadMoreStatus = 'loading';
        }
      })
      .addCase(loadMoreAppNotificationsForGroup.fulfilled, (state, action) => {
        const key = action.meta.arg.params?.groupKey;
        if (!key || !state.groupStates[key]) return;
        appNotificationsEntityAdapter.upsertMany(
          state.appNotifications,
          action.payload.notifications
        );
        state.groupStates[key].loadMoreStatus = 'success';
        state.groupStates[key].hasMore = action.payload.hasMore;
        state.groupStates[key].fetchedCount +=
          action.payload.notifications.length;
      })
      .addCase(loadMoreAppNotificationsForGroup.rejected, (state, action) => {
        const key = action.meta.arg.params?.groupKey;
        if (key && state.groupStates[key]) {
          state.groupStates[key].loadMoreStatus = 'failed';
        }
      });
  },
});
export const appNotificationActions = appNotificationSlice.actions;

// REDUCER =====================================================================
export const appNotificationReducer = appNotificationSlice.reducer;

// SELECTORS ===================================================================
const selectSliceState = (state): AppNotificationState =>
  (state as { appNotifications: AppNotificationState })?.appNotifications;

const selectAppNotificationGroups = () =>
  createSelector(selectSliceState, state => state.appNotificationGroups);

const selectAppNotificationAdapterState = state =>
  (state as { appNotifications: AppNotificationState })?.appNotifications
    ?.appNotifications;

const selectAppNotifications = createSelector(
  selectAppNotificationAdapterState,
  selectAppNotificationListFromAdapter
);

const selectAppNotificationById = (id: number) => state =>
  selectAppNotificationAdapterState(state)?.entities?.[id];

const selectGroupStates = (
  state
): Record<string, AppNotificationGroupPageState> =>
  selectSliceState(state)?.groupStates ?? {};

const selectGroupState =
  (groupKey: string) =>
  (state): AppNotificationGroupPageState | undefined =>
    selectSliceState(state)?.groupStates?.[groupKey];

const selectTotalCount = createSelector(selectSliceState, state =>
  Object.values(state.groupStates).reduce((sum, gs) => sum + gs.totalCount, 0)
);

const selectLoadingStatus = (state): LoadingStatus => {
  const groupStates = Object.values(selectGroupStates(state));
  if (groupStates.some(gs => gs.status === 'loading')) return 'loading';
  if (groupStates.some(gs => gs.status === 'failed')) return 'failed';
  if (
    groupStates.length > 0 &&
    groupStates.every(gs => gs.status === 'success')
  )
    return 'success';
  return 'initial';
};

export const appNotificationAppSelectors = {
  useSelectAppNotificationLoadingStatus: () =>
    useAppSelector(selectLoadingStatus),
  useSelectAppNotificationGroups: () =>
    useAppSelector(selectAppNotificationGroups()),
  useSelectAppNotifications: (): AppNotificationDto[] =>
    useAppSelector(selectAppNotifications),
  useSelectAppNotificationById: (id: number): AppNotificationDto =>
    useAppSelector(selectAppNotificationById(id)),
  useSelectGroupStates: () => useAppSelector(selectGroupStates),
  useSelectGroupState: (
    groupKey: string
  ): AppNotificationGroupPageState | undefined =>
    useAppSelector(selectGroupState(groupKey)),
  useSelectTotalCount: () => useAppSelector(selectTotalCount),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: verifyUser.fulfilled,
    effect: async ({ dispatch, state }) => {
      const { appNotificationGroups } = state.appNotifications;
      if (appNotificationGroups === null) {
        dispatch(getAppNotificationGroupTypes({}));
      } else {
        appNotificationGroups.forEach(g =>
          dispatch(
            getAppNotificationsForGroup({
              params: { groupKey: g.key, 'page-size': 10 },
            })
          )
        );
      }
    },
  });

  addEffect({
    action: getAppNotificationGroupTypes.fulfilled,
    effect: async ({ dispatch, action }) => {
      action.payload.notificationGroups.forEach(g =>
        dispatch(
          getAppNotificationsForGroup({
            params: { groupKey: g.key, 'page-size': 10 },
          })
        )
      );
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const appNotificationWebsocketActions: WebsocketEventActions[] = [
  {
    type: AppNotificationEventTypes.NOTIFICATION_UPSERTED,
    action: appNotificationSlice.actions.upsertAppNotifications,
    channel: 'baseSocket',
  },
  {
    type: AppNotificationEventTypes.NOTIFICATION_DELETED,
    action: appNotificationSlice.actions.deleteAppNotifications,
    channel: 'baseSocket',
  },
];
